import { FieldChange } from "./migration.diff"

/**
 * Migrations are declared HERE, in the frontend. Adding one is a single file under `registry/` —
 * no backend deploy, no module to learn. The API only offers a generic read-only probe
 * (`inspectCollection`), so everything that decides what a migration IS lives on this side:
 * which collection to look at, how to tell whether the work is done, what the preview looks like,
 * and what it is called in either language.
 *
 * The one thing that does NOT live here is the mutation itself. Running goes through the existing
 * `OneTimesType` case in api-v2, which is reviewed code — a write posted from a browser would turn
 * any XSS in this panel into unrestricted data loss.
 */

export type MigrationStatus = "APPLIED" | "PENDING" | "PARTIAL" | "UNKNOWN"
export type MigrationRisk = "SAFE" | "INDEX" | "DESTRUCTIVE"
export type MigrationIndexAction = "DROP" | "CREATE" | "KEEP"

/**
 * Free-form Mongo filter. `$where`/`$function`/`$accumulator`/`$out`/`$merge` are refused
 * server-side, and a `{ $idsOf: [...] }` node is resolved there — see `idsOf`.
 */
export type MongoFilter = Record<string, unknown>

/**
 * Stands in for a list of ids a filter cannot spell out: the server replaces it with the `_id`s of
 * the named collections, as strings, before the query runs. Only the marketplace account
 * collections and `EmailAccounts` are allowed (api-v2 `MIGRATION_ID_REF_COLLECTIONS`); anything
 * else is refused.
 *
 * Needed because a channel reference carries no platform beside it — a guidance row's `channel`
 * and a skill's `scope.channelIds` are account ids, so "is this a marketplace (or e-mail)
 * channel?" can only be answered by looking the id up.
 */
export type MongoIdRef = { $idsOf: string[] }

export function idsOf(collections: ReadonlyArray<string>): MongoIdRef {
  return { $idsOf: [...collections] }
}

export type ExpectedIndex = {
  /** Key spec exactly as Mongo stores it, e.g. `{ customer: 1, role: 1 }`. */
  keys: Record<string, 1 | -1>
  unique?: boolean
}

export type BilingualText = {
  en: string
  tr: string
}

type MigrationSpecBase = {
  /** The existing api-v2 `OneTimesType` that performs the migration. */
  type: string
  title: BilingualText
  /** What it does, in the technical terms an operator needs before pressing run. */
  description: BilingualText
  risk: MigrationRisk
  collection: string
  /** Indexes the migration removes, and the ones it leaves behind. Optional. */
  indexes?: {
    mustDrop?: ExpectedIndex[]
    mustExist?: ExpectedIndex[]
  }
  /** Extra things the operator must know. Shown as warnings, never collapsed. */
  notes?: BilingualText[]
}

/** Rewrites documents that exist: the work left is whatever `affectedFilter` still matches. */
export type UpdateMigrationSpec = MigrationSpecBase & {
  /** Documents this migration has NOT been applied to yet. Empty status when it matches nothing. */
  affectedFilter: MongoFilter
  /** Fields the preview needs. A probe never pulls whole documents into the browser. */
  sampleFields: string[]
  /**
   * Runs IN THE BROWSER over each sampled document to show what the migration would make of it.
   * Pure — it never talks to anything.
   */
  preview?: (doc: Record<string, unknown>) => Record<string, unknown>
  expectedDocuments?: never
}

/**
 * Inserts documents that must exist (templates, seeds). A missing document cannot be counted, so
 * the work left is measured from the other side: APPLIED once at least `count` documents match
 * `filter`.
 */
export type SeedMigrationSpec = MigrationSpecBase & {
  expectedDocuments: { filter: MongoFilter; count: number }
  affectedFilter?: never
  sampleFields?: never
  preview?: never
}

export type MigrationSpec = UpdateMigrationSpec | SeedMigrationSpec

export function isSeedSpec(spec: MigrationSpec): spec is SeedMigrationSpec {
  return spec.expectedDocuments !== undefined
}

export type MigrationIndexChange = {
  name: string
  keys: string
  unique: boolean
  action: MigrationIndexAction
  present: boolean
}

export type MigrationSample = {
  id: string
  before: string
  after: string
  /** Empty when the spec has no `preview` — nothing to compare against. */
  changes: FieldChange[]
}

export type MigrationRunStatus =
  | "RUNNING"
  | "SUCCEEDED"
  | "FAILED"
  | "INTERRUPTED"

export type MigrationRun = {
  _id: string
  type: string
  status: MigrationRunStatus
  succeeded?: boolean | null
  startedAt: string
  finishedAt?: string | null
  durationMs?: number | null
  heartbeatAt?: string | null
  countsBefore?: string | null
  countsAfter?: string | null
  result?: string | null
  error?: string | null
}

/** What the page computes for one spec, entirely from the probe result. */
export type MigrationState = {
  spec: MigrationSpec
  status: MigrationStatus
  /** For a seed spec: how many expected documents are still missing. */
  affectedCount: number
  totalCount: number
  /** Seed specs only: documents matching `expectedDocuments.filter`. */
  presentCount?: number
  indexChanges: MigrationIndexChange[]
  samples: MigrationSample[]
  /** Populated when the probe itself failed — the row degrades, the page still renders. */
  probeError?: string
  lastRun?: MigrationRun
}

export const STATUS_COLOR: Record<
  MigrationStatus,
  "success" | "warning" | "error" | "default"
> = {
  APPLIED: "success",
  PENDING: "warning",
  PARTIAL: "error",
  UNKNOWN: "default"
}

export const RISK_COLOR: Record<
  MigrationRisk,
  "default" | "warning" | "error"
> = {
  SAFE: "default",
  INDEX: "warning",
  DESTRUCTIVE: "error"
}

export const INDEX_ACTION_COLOR: Record<
  MigrationIndexAction,
  "error" | "success" | "default"
> = {
  DROP: "error",
  CREATE: "success",
  KEEP: "default"
}

export function hasOutstandingWork(state: MigrationState): boolean {
  return state.status === "PENDING" || state.status === "PARTIAL"
}

/** The read-only probe contract. Owned here because the page decides what to ask for. */
export type InspectCollectionInput = {
  collection: string
  counts?: { key: string; filter?: MongoFilter }[]
  withIndexes?: boolean
  /** Collection size from metadata — a count with an empty filter scans every document. */
  estimatedTotal?: boolean
  sample?: { filter?: MongoFilter; fields: string[]; limit?: number }
}

export type InspectCollectionResult = {
  collection: string
  counts: { key: string; count: number }[]
  indexes: { name: string; keys: string; unique: boolean }[]
  samples: string[]
  estimatedTotal?: number | null
}

/**
 * Injected rather than imported, so the probe engine stays pure logic with no transport of its
 * own — which is also what makes it testable without mocking a module.
 */
export type ProbeFetcher = (
  input: InspectCollectionInput
) => Promise<InspectCollectionResult>

export function pickText(text: BilingualText, language: string): string {
  return language?.startsWith("tr") ? text.tr : text.en
}

export const RUN_STATUS_COLOR: Record<
  MigrationRunStatus,
  "info" | "success" | "error" | "warning"
> = {
  RUNNING: "info",
  SUCCEEDED: "success",
  FAILED: "error",
  INTERRUPTED: "warning"
}

/**
 * One table row: the spec, where it sits in run order, and whatever is known so far. `state` is
 * missing until its own probe answers — rows load one by one, never all-or-nothing.
 */
export type MigrationRow = {
  spec: MigrationSpec
  order: number
  archived: boolean
  state?: MigrationState
  probing: boolean
  lastRun?: MigrationRun
}

export type MigrationSection = "RUNNING" | "ATTENTION" | "APPLIED"

/** The database decides APPLIED, never the run log — a failed run that something else fixed is done. */
export function sectionOf(row: MigrationRow): MigrationSection {
  if (row.lastRun?.status === "RUNNING") return "RUNNING"
  if (row.state?.status === "APPLIED") return "APPLIED"
  return "ATTENTION"
}

/** `countsBefore`/`countsAfter` as the server stored them: a JSON array of `{ key, count }`. */
export function runCount(
  stored: string | null | undefined,
  key: string
): number | undefined {
  if (!stored) return undefined
  try {
    const counts = JSON.parse(stored) as { key: string; count: number }[]
    return counts.find((count) => count.key === key)?.count
  } catch {
    return undefined
  }
}

export function formatElapsed(ms: number): string {
  const totalSeconds = Math.max(0, Math.floor(ms / 1000))
  const hours = Math.floor(totalSeconds / 3600)
  const minutes = Math.floor((totalSeconds % 3600) / 60)
  const seconds = totalSeconds % 60
  const pad = (value: number) => String(value).padStart(2, "0")
  return hours > 0
    ? `${hours}:${pad(minutes)}:${pad(seconds)}`
    : `${minutes}:${pad(seconds)}`
}
